import { RenderToken, Token } from './parser';
import { seededHash, randomBetweenFromSeed, randomUnitFromSeed } from './util';

export const promptMarker = '> ';
const typingCharsPerSecond = 15;
const glitch = createGlitch();

/**
 * Immutable data describing the current render frame and line.
 * - All timing is in milliseconds.
 */
type RenderEnv = {
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  now: number;
  cycleTick: number;
  charWidth: number;
  textHeight: number;
  nextClock: number;
  loadTime: number;
  lineSpacing: number;
  cursorY: number;
  /**
   * Optional hooks for side effects kept outside of shared code (e.g., audio).
   */
  onTypingChar?: () => void;
};

/**
 * Mutable state that token renderers can read and update.
 * - x: current pen position on the X axis (in pixels).
 * - consumedMs: how much of the lineBudgetMs has been consumed by tokens already.
 */
type FrameState = {
  x: number;
  consumedMs: number;
  /**
   * The time budget available for this line during the current frame.
   * Token renderers should consume from this via consumedMs.
   */
  lineBudgetMs: number;
  /**
   * Line index within the full list of lines being rendered.
   * Used for deterministic effects (e.g., UNREADABLE_GLITCH).
   */
  globalIndex: number;
};

export function drawToken(token: RenderToken, env: RenderEnv, state: FrameState) {
  switch (token.kind) {
    case Token.Text:
      return renderText(token, env, state);
    case Token.FlashText:
      return renderFlashText(token, env, state);
    case Token.Wait:
      return consumeWait(token, env, state);
    case Token.Loading:
      return renderLoadingBar(token, env, state);
    case Token.Typing:
      return renderTyping(token, env, state);
    case Token.Glitch:
      return renderGlitch(token, env, state);
    case Token.UnreadableGlitch:
      return renderUnreadableGlitch(token, env, state);
    case Token.Cycle:
      return renderCycle(token, env, state);
  }
}

export function renderText(
  token: Extract<RenderToken, { kind: typeof Token.Text }>,
  env: RenderEnv,
  state: FrameState,
) {
  const { text } = token;
  if (text.length === 0) return;
  env.ctx.fillText(text, state.x, env.cursorY);
  state.x += env.charWidth * text.length;
}

export function renderFlashText(
  token: Extract<RenderToken, { kind: typeof Token.FlashText }>,
  env: RenderEnv,
  state: FrameState,
) {
  const { text } = token;
  if (text.length === 0) return;
  const cycleDuration = 1000;
  const cyclePosition = env.now % cycleDuration;
  const flashOn = cyclePosition < 750;
  if (flashOn) {
    env.ctx.fillText(text, state.x, env.cursorY);
  }
  state.x += env.charWidth * text.length;
}

export function consumeWait(
  token: Extract<RenderToken, { kind: typeof Token.Wait }>,
  env: RenderEnv,
  state: FrameState,
) {
  const waitMs = token.time;
  const remaining = state.lineBudgetMs - state.consumedMs;
  if (remaining <= 0) return;
  state.consumedMs += waitMs;
}

export function renderLoadingBar(
  token: Extract<RenderToken, { kind: typeof Token.Loading }>,
  env: RenderEnv,
  state: FrameState,
) {
  const totalMs = token.time > 0 ? token.time : 0;
  const remaining = state.lineBudgetMs - state.consumedMs;
  if (remaining <= 0) return;

  const timeInto = remaining < totalMs ? remaining : totalMs;
  const progress = totalMs === 0 ? 1 : timeInto / totalMs;

  const loadingBarSize = 20;
  const filled = Math.floor(loadingBarSize * progress);
  const spaces = loadingBarSize - filled;
  const percent = Math.round(progress * 100);
  const bar = `${'█'.repeat(filled)}${' '.repeat(spaces)} ${percent}%`;

  env.ctx.fillText(bar, state.x, env.cursorY);
  state.x += env.charWidth * bar.length;

  // Gate subsequent tokens for the full duration
  state.consumedMs += totalMs;
}

export function renderTyping(
  token: Extract<RenderToken, { kind: typeof Token.Typing }>,
  env: RenderEnv,
  state: FrameState,
) {
  const chars = Array.from(token.text);
  const totalChars = chars.length;
  const cps = typingCharsPerSecond;
  const totalMs = totalChars > 0 && cps > 0 ? (totalChars * 1000) / cps : 0;

  const remaining = state.lineBudgetMs - state.consumedMs;
  if (remaining <= 0) return;

  const timeInto = remaining < totalMs ? remaining : totalMs;
  const revealed = totalChars > 0 && cps > 0 ? Math.min(totalChars, Math.floor((timeInto * cps) / 1000)) : totalChars;

  // per-character side effects
  const newlyRevealed = revealed - token.playedCount;
  if (newlyRevealed > 0 && typeof env.onTypingChar === 'function') {
    for (let i = 0; i < newlyRevealed; i++) {
      env.onTypingChar();
    }
  }
  token.playedCount = revealed;

  const visible = chars.slice(0, revealed).join('');
  if (visible.length > 0) {
    env.ctx.fillText(visible, state.x, env.cursorY);
    state.x += env.charWidth * visible.length;
  }

  // Gate subsequent tokens for the full duration
  state.consumedMs += totalMs;
}

export function renderGlitch(
  token: Extract<RenderToken, { kind: typeof Token.Glitch }>,
  env: RenderEnv,
  state: FrameState,
) {
  const chars = Array.from(token.text);
  glitch.init(token, chars, env.now);
  glitch.update(token, chars, env.now, env.cycleTick);

  for (let i = 0; i < chars.length; i++) {
    const idxInList = token.glitchIndices.indexOf(i);
    const endAt = idxInList >= 0 ? token.glitchEndAt[idxInList] : undefined;

    let ch = chars[i] ?? '';
    if (idxInList !== -1 && endAt !== undefined && env.now < endAt) {
      const h = seededHash(token.seed, i, env.cycleTick);
      const junk = glitch.junkChars[h % glitch.junkChars.length];
      ch = junk !== undefined ? junk : ch;
    }

    env.ctx.fillText(ch, state.x, env.cursorY);
    state.x += env.charWidth;
  }
}

export function renderUnreadableGlitch(
  token: Extract<RenderToken, { kind: typeof Token.UnreadableGlitch }>,
  env: RenderEnv,
  state: FrameState,
) {
  const safeLength = Number.isFinite(token.length) && token.length > 0 ? Math.floor(token.length) : 0;
  if (safeLength <= 0) return;

  const colStart = Math.floor(state.x / env.charWidth);
  for (let j = 0; j < safeLength; j++) {
    const h = seededHash(state.globalIndex, env.cycleTick, colStart + j);
    const junk = glitch.junkChars[h % glitch.junkChars.length];
    const ch = junk !== undefined ? junk : ' ';
    env.ctx.fillText(ch, state.x, env.cursorY);
    state.x += env.charWidth;
  }
}

export function renderCycle(
  token: Extract<RenderToken, { kind: typeof Token.Cycle }>,
  env: RenderEnv,
  state: FrameState,
) {
  const frames = token.frames;
  if (frames.length === 0) return;
  const frameIndex = env.cycleTick % frames.length;
  const frameSafe = frames[frameIndex] ?? '';
  if (frameSafe.length === 0) return;

  env.ctx.fillText(frameSafe, state.x, env.cursorY);
  state.x += env.charWidth * frameSafe.length;
}

function createGlitch() {
  type GlitchToken = Extract<RenderToken, { kind: typeof Token.Glitch }>;

  // Avoid chars %(,) since they're used by the parser
  const junkChars =
    '!!!!!$$$$$$$$$$@@@@@#####&&&&&^^^^^*****+++++-----=====/////\\\\\\\\|||||<<<<<>>>>>?????^^^^^~~~~~;:;:;:.....-_+=*@$#&!0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  // Duration for an individual character to be actively glitched
  const glitchMinMs = 200;
  const glitchMaxMs = 1200;

  // Reselection behavior constants
  const glitchReselectionTauMs = 12000; // decay time constant
  const glitchReselectionPMax = 0.91; // initial reselection probability
  const glitchReselectionPMin = 0.03; // floor (set to 0 if you want it to fully stop)
  const glitchMaxConcurrentRatio = 0.15; // max ~15% of non-whitespace chars concurrently glitched

  const pickGlitchIndices = (chars: readonly string[], seed: number, ratio: number): number[] => {
    const candidates: number[] = [];
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i];
      if (ch !== undefined && ch.trim().length > 0) {
        candidates.push(i);
      }
    }
    const count = Math.floor(candidates.length * ratio);
    if (count <= 0) return [];
    const scored = candidates
      .map((idx) => ({ idx, score: seededHash(seed, idx, 12345) }))
      .sort((a, b) => {
        if (a.score < b.score) return -1;
        if (a.score > b.score) return 1;
        return 0;
      });
    const out: number[] = [];
    for (let i = 0; i < scored.length && out.length < count; i++) {
      const s = scored[i];
      if (s !== undefined) out.push(s.idx);
    }
    return out;
  };

  const init = (token: GlitchToken, chars: readonly string[], now: number) => {
    if (token.startedAt !== null) return;
    const seed = (Math.random() * 0x7fffffff) >>> 0;
    const initial = pickGlitchIndices(chars, seed, glitchMaxConcurrentRatio);
    const ends: number[] = [];
    for (let i = 0; i < initial.length; i++) {
      const idx = initial[i];
      if (idx !== undefined) {
        ends.push(now + randomBetweenFromSeed(glitchMinMs, glitchMaxMs, seed, idx));
      }
    }
    token.startedAt = now;
    token.seed = seed;
    token.glitchIndices = initial;
    token.glitchEndAt = ends;
  };

  const update = (token: GlitchToken, chars: readonly string[], now: number, cycleTick: number) => {
    const nonWhitespaceCount = chars.reduce((n, ch) => n + (ch.trim().length > 0 ? 1 : 0), 0);
    const maxConcurrent = Math.floor(nonWhitespaceCount * glitchMaxConcurrentRatio);

    const keptI: number[] = [];
    const keptE: number[] = [];
    const activeSet = new Set<number>();

    for (let j = 0; j < token.glitchIndices.length; j++) {
      const idx = token.glitchIndices[j];
      const endAt = token.glitchEndAt[j];
      if (idx !== undefined && endAt !== undefined && now < endAt) {
        keptI.push(idx);
        keptE.push(endAt);
        activeSet.add(idx);
      }
    }

    const startedAtSafe = token.startedAt !== null ? token.startedAt : now;
    const elapsed = now - startedAtSafe;
    const p =
      glitchReselectionPMin +
      (glitchReselectionPMax - glitchReselectionPMin) * Math.exp(-elapsed / glitchReselectionTauMs);

    const expiredSlots = token.glitchIndices.length - keptI.length;
    let s = 0;
    for (let k = 0; k < expiredSlots; k++) {
      if (keptI.length >= maxConcurrent) break;

      const r = randomUnitFromSeed(token.seed, cycleTick, s);
      s += 1;

      if (r < p) {
        const start = seededHash(token.seed, cycleTick, k + 77) % (chars.length > 0 ? chars.length : 1);
        let chosen: number | null = null;

        const limit = Math.min(chars.length, 32);
        for (let o = 0; o < limit; o++) {
          const cand = (start + o * 7) % (chars.length > 0 ? chars.length : 1);
          const ch = chars[cand];
          if (ch !== undefined && ch.trim().length > 0 && !activeSet.has(cand)) {
            chosen = cand;
            break;
          }
        }

        if (chosen !== null) {
          activeSet.add(chosen);
          keptI.push(chosen);
          keptE.push(now + randomBetweenFromSeed(glitchMinMs, glitchMaxMs, token.seed, chosen + cycleTick));
        }
      }
    }

    token.glitchIndices = keptI;
    token.glitchEndAt = keptE;
  };

  return { init, update, junkChars };
}

// todo: could merge this with the other loading bar drawing fn
export function drawInitialLoadingBar(ctx: OffscreenCanvasRenderingContext2D, env: RenderEnv) {
  // text loading bar
  // ██████████████████████ 50%
  const loadProgress = Math.min(1, env.nextClock / env.loadTime);
  const loadingBarSize = 38;
  const equalSigns = Math.floor(loadingBarSize * loadProgress);
  const spaces = loadingBarSize - equalSigns;

  const maxDotAmount = 3;
  const dotAmount = Math.floor((env.nextClock * 0.006) % (maxDotAmount + 1));
  const lines = [
    `${'█'.repeat(equalSigns)}${' '.repeat(spaces)} ${Math.round(loadProgress * 100)}%`,
    `Booting${'.'.repeat(dotAmount)}`,
  ];

  for (const line of lines) {
    ctx.fillText(line, 0, env.cursorY);
    env.cursorY += env.textHeight + env.lineSpacing;
  }
}
